// Что панель ведомости знает о происходящем на экране.
//
// Панели рисует каркас, и от экрана им достаётся только идентификатор из адреса. Остальное они
// берут ОТТУДА ЖЕ, откуда экран: ведомость — из `?f.sheet`, строки и людей — из той же сетки.
// Своего запроса за одной строкой в контракте нет, и заводить в панели вторую копию состояния
// значит однажды показать в ней сумму, которой в сетке уже нет.

import { useLocation } from 'react-router';

import type {
  PayrollEmployeeOut,
  PayrollGridOut,
  PayrollRowOut,
} from '@/shared/api/endpoints/payroll';
import { useAuthStore } from '@/shared/auth/store';
import { can } from '@/shared/rbac/permissions';
import { parseSearch } from '@/shared/routing/search';

import { FILTER_SHEET } from '../../route';
import { useSheetGrid } from '../queries';

/** Ведомость, открытая на экране. Пусто — ни одна не выбрана. */
export function useCurrentSheetId(): string | null {
  const location = useLocation();
  const явная = parseSearch(location.search).filters[FILTER_SHEET];
  return явная === undefined || явная === '' ? null : явная;
}

/** Право видеть ведомости. То же, что у экрана: панель рисует каркас, а не экран. */
const VIEW_PERMISSION = 'core.payroll.view';

export interface СеткаПанели {
  readonly grid: PayrollGridOut | undefined;
  readonly isPending: boolean;
  readonly error: unknown;
  /**
   * Почему показывать нечего, словами. Пусто — всё в порядке.
   *
   * Нужно потому, что у выключенного запроса состояние навсегда остаётся «ожидание»: панель без
   * этой развилки крутила бы скелет вечно, а вечная загрузка неотличима от сломанной программы.
   */
  readonly nothing: string | null;
  /**
   * Повторить запрос.
   *
   * Ошибка ЗАПРОСА без повтора оставляет человеку единственный выход — перезагрузить страницу,
   * а вместе с ней уходит вся открытая стопка панелей и незаконченная работа.
   */
  readonly retry: () => void;
}

/** Сетка открытой ведомости — та же, что видит экран, без второго запроса. */
export function useSheetFromScreen(): СеткаПанели {
  const sheetId = useCurrentSheetId();
  const права = useAuthStore((state) => state.permissions);
  const можноСмотреть = can(права, VIEW_PERMISSION);
  const сетка = useSheetGrid(sheetId, можноСмотреть);
  return {
    grid: сетка.data,
    isPending: сетка.isPending,
    error: сетка.error,
    nothing: !можноСмотреть
      ? 'Ведомости вам не показываются: их видят финансовый отдел, руководство и администратор.'
      : sheetId === null
        ? 'Ведомость не выбрана, а панель открывается по ней. Выберите ведомость в списке слева — панель наполнится сама.'
        : null,
    retry: () => void сетка.refetch(),
  };
}

/** Тарифная строка по идентификатору вместе с её человеком. */
export function findRow(
  grid: PayrollGridOut | undefined,
  rowId: string,
): { row: PayrollRowOut; employee: PayrollEmployeeOut } | null {
  for (const человек of grid?.employees ?? []) {
    const строка = (человек.rows ?? []).find((пункт) => пункт.id === rowId);
    if (строка !== undefined) {
      return { row: строка, employee: человек };
    }
  }
  return null;
}

/** Человек в ведомости по идентификатору его расчётной записи. */
export function findEmployee(
  grid: PayrollGridOut | undefined,
  employeeId: string,
): PayrollEmployeeOut | null {
  return (grid?.employees ?? []).find((человек) => человек.id === employeeId) ?? null;
}
